<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Comparativo por Tipo de Canal',
  },
});

const sortedData = computed(() => {
  if (!props.data || props.data.length === 0) return [];
  return [...props.data].sort(
    (a, b) => b.conversations_count - a.conversations_count
  );
});

const maxConversations = computed(() => {
  if (sortedData.value.length === 0) return 1;
  return Math.max(...sortedData.value.map(c => c.conversations_count), 1);
});

const totalConversations = computed(() => {
  return sortedData.value.reduce((sum, c) => sum + c.conversations_count, 0);
});

function formatTime(minutes) {
  if (!minutes || minutes === 0) return '-';
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  return `${(minutes / 60).toFixed(1)}h`;
}

function getChannelIcon(channelType) {
  const icons = {
    'Channel::WebWidget': 'i-lucide-globe',
    'Channel::Api': 'i-lucide-code',
    'Channel::Whatsapp': 'i-lucide-message-circle',
    'Channel::FacebookPage': 'i-lucide-facebook',
    'Channel::TwitterProfile': 'i-lucide-twitter',
    'Channel::Email': 'i-lucide-mail',
    'Channel::Telegram': 'i-lucide-send',
  };
  return icons[channelType] || 'i-lucide-inbox';
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-bar-chart-2 text-lg text-n-violet-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-64">
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="sortedData.length === 0"
      class="flex items-center justify-center h-64 text-n-slate-11"
    >
      <span class="text-sm">Sem dados de canais</span>
    </div>

    <!-- Chart -->
    <div v-else class="flex flex-col gap-4">
      <div
        v-for="channel in sortedData"
        :key="channel.channel_type"
        class="flex flex-col gap-2"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span
              :class="getChannelIcon(channel.channel_type)"
              class="text-lg"
              :style="{ color: channel.color }"
            />
            <span class="text-sm font-medium text-n-slate-12">{{
              channel.channel_name
            }}</span>
            <span class="text-xs text-n-slate-10">({{ channel.inboxes_count }} inbox{{
                channel.inboxes_count !== 1 ? 'es' : ''
              }})</span>
          </div>
          <div class="flex items-center gap-3 text-xs">
            <span class="text-n-slate-11">{{ channel.percentage }}%</span>
            <span class="font-bold text-n-slate-12">{{
              channel.conversations_count
            }}</span>
          </div>
        </div>

        <!-- Progress bar -->
        <div
          class="h-6 bg-n-alpha-1 rounded-lg overflow-hidden flex items-center"
        >
          <div
            class="h-full rounded-lg transition-all duration-300 flex items-center justify-end px-2"
            :style="{
              width: `${(channel.conversations_count / maxConversations) * 100}%`,
              backgroundColor: channel.color,
              minWidth: channel.conversations_count > 0 ? '40px' : '0',
            }"
          >
            <span
              v-if="channel.conversations_count > 0"
              class="text-xs text-white font-medium"
            >
              {{ channel.resolved_count }} res.
            </span>
          </div>
        </div>

        <!-- Metrics row -->
        <div class="grid grid-cols-4 gap-2 text-xs">
          <div class="text-center p-1.5 rounded bg-n-alpha-1">
            <div class="font-medium text-n-teal-11">
              {{ channel.resolution_rate }}%
            </div>
            <div class="text-n-slate-10">Resolucao</div>
          </div>
          <div class="text-center p-1.5 rounded bg-n-alpha-1">
            <div class="font-medium text-n-blue-11">
              {{ formatTime(channel.avg_first_response_minutes) }}
            </div>
            <div class="text-n-slate-10">1a Resp.</div>
          </div>
          <div class="text-center p-1.5 rounded bg-n-alpha-1">
            <div class="font-medium text-n-amber-11">
              {{ channel.csat_score || 0 }}%
            </div>
            <div class="text-n-slate-10">CSAT</div>
          </div>
          <div class="text-center p-1.5 rounded bg-n-alpha-1">
            <div class="font-medium text-n-slate-12">
              {{ channel.messages_count }}
            </div>
            <div class="text-n-slate-10">Mensagens</div>
          </div>
        </div>
      </div>

      <!-- Total -->
      <div
        class="flex items-center justify-between pt-3 border-t border-n-weak text-sm"
      >
        <span class="text-n-slate-11">Total no periodo</span>
        <span class="font-bold text-n-slate-12">{{ totalConversations }} conversas</span>
      </div>
    </div>
  </div>
</template>
